/**
 * Records service with consistent API response format and full TypeScript support
 * Provides comprehensive records management functionality including CRUD operations,
 * pagination, search, batch operations, and legacy compatibility methods.
 */

import api from './apiService';
import { ApiResponse, Record, RecordsQueryFilters, RecordsApiResponse } from '../types/apiTypes';
import { parseConsistentApiResponse } from '../utils/consistentApiParser';

class RecordsService {
  /**
   * Query records with filters and pagination
   */
  async queryRecords(filters: RecordsQueryFilters = {}, retryCount: number = 0): Promise<RecordsApiResponse<Record>> {
    if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'test') {
      console.log('recordsService: Querying records with filters:', filters);
      console.log('recordsService: Retry count:', retryCount);
    }

    try {
      // Ensure limit is a number if present
      const processedFilters = { ...filters };
      if (processedFilters.limit) {
        processedFilters.limit = Number(processedFilters.limit);
      }

      // Add cache-busting timestamp parameter
      const cacheBustingParams = {
        ...processedFilters,
        _t: Date.now()
      };

      const response = await api.get('/records', {
        params: cacheBustingParams
      });

      const apiResponse = parseConsistentApiResponse<Record[]>(
        response,
        'recordsService',
        'queryRecords'
      ) as RecordsApiResponse<Record>;

      if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'test') {
        console.log('recordsService: Parsed response:', apiResponse);
      }

      // Implement GSI retry logic for leader_id queries
      if (filters.leader_id &&
          apiResponse.data &&
          Array.isArray(apiResponse.data) &&
          apiResponse.data.length === 0 &&
          apiResponse.query_method === 'leader_id_gsi' &&
          retryCount < 2) {

        if (process.env.NODE_ENV === 'development' || process.env.REACT_APP_ENV === 'test') {
          console.log(`recordsService: No results from GSI for leader_id ${filters.leader_id}, retrying in 2 seconds (attempt ${retryCount + 1}/2)`);
        }

        // Wait 2 seconds and retry (GSI indexing delay)
        await new Promise(resolve => setTimeout(resolve, 2000));
        return this.queryRecords(filters, retryCount + 1);
      }

      return apiResponse;
    } catch (error) {
      console.error('recordsService: Error querying records:', error);
      throw error;
    }
  }

  /**
   * Get record by ID
   */
  async getRecordById(recordId: string): Promise<ApiResponse<Record>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Getting record by ID:', recordId);
    }

    const response = await api.get(`/records/${recordId}`);
    return parseConsistentApiResponse<Record>(response, 'recordsService', 'getRecordById');
  }

  /**
   * Create new record
   */
  async createRecord(recordData: Partial<Record>): Promise<ApiResponse<Record>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Creating record with data:', recordData);
    }

    const response = await api.post('/records', recordData);
    return parseConsistentApiResponse<Record>(response, 'recordsService', 'createRecord');
  }

  /**
   * Update record
   */
  async updateRecord(id: string, recordData: Partial<Record>): Promise<ApiResponse<Record>> {
    if (process.env.NODE_ENV === 'development') {
      console.log(`recordsService: Updating record ${id} with data:`, recordData);
    }

    const response = await api.put(`/records/${id}`, recordData);
    return parseConsistentApiResponse<Record>(response, 'recordsService', 'updateRecord');
  }

  /**
   * Delete record
   */
  async deleteRecord(id: string): Promise<ApiResponse<{ record_id: string }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log(`recordsService: Deleting record: ${id}`);
    }

    const response = await api.delete(`/records/${id}`);
    return parseConsistentApiResponse<{ record_id: string }>(response, 'recordsService', 'deleteRecord');
  }

  /**
   * Upload records file
   */
  async uploadRecords(file: File, channelId?: string): Promise<ApiResponse<{ uploaded_count: number; errors?: any[] }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Uploading records file:', file.name);
    }

    const formData = new FormData();
    formData.append('file', file);
    if (channelId) {
      formData.append('channel_id', channelId);
    }

    const response = await api.post('/records/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });

    return parseConsistentApiResponse<{ uploaded_count: number; errors?: any[] }>(
      response,
      'recordsService',
      'uploadRecords'
    );
  }

  /**
   * Get records with presigned URLs for file downloads
   */
  async getRecordsWithPresignedUrls(filters: RecordsQueryFilters = {}): Promise<RecordsApiResponse<Record>> {
    // Silent operation for thumbnail loading

    const response = await api.post('/records', {
      request_type: 'presigned_urls',
      ...filters
    });

    const apiResponse = parseConsistentApiResponse<Record[]>(
      response,
      'recordsService',
      'getRecordsWithPresignedUrls'
    ) as RecordsApiResponse<Record>;

    // Logging disabled to reduce console noise

    return apiResponse;
  }

  /**
   * Get records statistics
   */
  async getRecordsStats(filters: RecordsQueryFilters = {}): Promise<ApiResponse<{
    total_records: number;
    date_range: { start: string; end: string };
    sources: Array<{ source_id: string; count: number }>;
    channels: Array<{ channel_id: string; count: number }>;
  }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Getting records statistics, filters:', filters);
    }

    const response = await api.get('/records/stats', {
      params: filters
    });

    return parseConsistentApiResponse(response, 'recordsService', 'getRecordsStats');
  }

  /**
   * Export records to file
   */
  async exportRecords(
    filters: RecordsQueryFilters = {},
    format: 'json' | 'csv' | 'xml' = 'json'
  ): Promise<ApiResponse<{ download_url: string; expires_at: string }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Exporting records, filters:', filters, 'format:', format);
    }

    const response = await api.post('/records/export', {
      filters,
      format
    });

    return parseConsistentApiResponse(response, 'recordsService', 'exportRecords');
  }

  /**
   * Batch delete records
   */
  async batchDeleteRecords(recordIds: string[]): Promise<ApiResponse<{ deleted_count: number; deleted_ids: string[] }>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Batch deleting records:', recordIds);
    }

    const response = await api.delete('/records/batch', {
      data: { record_ids: recordIds }
    });

    return parseConsistentApiResponse(response, 'recordsService', 'batchDeleteRecords');
  }

  /**
   * Get records timeline/aggregation
   */
  async getRecordsTimeline(
    filters: RecordsQueryFilters = {},
    interval: 'hour' | 'day' | 'week' | 'month' = 'day'
  ): Promise<ApiResponse<Array<{ timestamp: string; count: number }>>> {
    if (process.env.NODE_ENV === 'development') {
      console.log('recordsService: Getting records timeline, filters:', filters, 'interval:', interval);
    }

    const response = await api.get('/records/timeline', {
      params: {
        ...filters,
        interval
      }
    });

    return parseConsistentApiResponse(response, 'recordsService', 'getRecordsTimeline');
  }

  // Legacy compatibility methods

  /**
   * Legacy method: Get records (alias for queryRecords)
   * @deprecated Use queryRecords instead
   */
  async getRecords(filters: RecordsQueryFilters = {}): Promise<any> {
    const response = await this.queryRecords(filters);
    // Return in legacy format for compatibility
    return {
      records: response.data || [],
      count: response.count || (response.data ? response.data.length : 0)
    };
  }

  /**
   * Legacy method: Get single record (alias for getRecordById)
   * @deprecated Use getRecordById instead
   */
  async getRecord(recordId: string): Promise<any> {
    const response = await this.getRecordById(recordId);
    return response.data;
  }

  /**
   * Legacy method: Upload single record
   * @deprecated Use uploadRecords instead
   */
  async uploadRecord(formData: FormData, onProgress?: (progress: number) => void): Promise<any> {
    // Extract file from FormData
    const file = formData.get('file') as File;
    const channelId = formData.get('channel_id') as string;

    const response = await this.uploadRecords(file, channelId);
    return response.data;
  }
}

// Export singleton instance
const recordsService = new RecordsService();
export default recordsService;
